import {
  CreatePrivacyRequestSchema,
  PrivacyRightSchema,
  PrivacySelectorSchema,
  type PrivacyRight,
  type PrivacySelector,
} from '@devmil/contracts';
import {useId, useState, type FormEvent} from 'react';

import {usePanel} from '../../app/PanelProvider.js';
import {describeProblem} from '../../lib/panel-problem.js';

/**
 * Abertura de um pedido do titular.
 *
 * O seletor é ENTRADA e nunca volta: ele carrega o que aponta para a pessoa
 * (identificador da conversa, pseudônimo ou telefone) e não aparece em nenhum
 * DTO de resposta. Aqui ele segue a regra dos outros segredos desta entrega:
 * vive no estado do formulário, vai no corpo do POST, e some no sucesso.
 */

const DIREITO_LABEL: Record<PrivacyRight, string> = {
  access: 'Acesso aos dados',
  correction: 'Correção',
  opposition: 'Oposição',
  consent_revocation: 'Revogação de consentimento',
  erasure: 'Exclusão',
};

type SelectorType = PrivacySelector['type'];

const SELETOR_LABEL: Record<SelectorType, string> = {
  conversation_id: 'Identificador da conversa',
  instagram_pseudonym: 'Pseudônimo do MAPA',
  whatsapp_phone: 'Telefone do WhatsApp, em E.164',
};

const CANAL_LABEL = {
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
} as const;

export const PrivacyRequestForm = ({onCreated}: {onCreated: () => void}) => {
  const {dataSource} = usePanel();
  const rightId = useId();
  const selectorTypeId = useId();
  const selectorValueId = useId();
  const viaId = useId();
  const receivedAtId = useId();
  const headingId = useId();

  const [right, setRight] = useState<PrivacyRight>('access');
  const [selectorType, setSelectorType] = useState<SelectorType>(
    'instagram_pseudonym',
  );
  const [selectorValue, setSelectorValue] = useState('');
  const [receivedVia, setReceivedVia] =
    useState<keyof typeof CANAL_LABEL>('instagram');
  const [receivedAt, setReceivedAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    // O seletor pode ser um telefone. Envio nativo o levaria para a URL.
    event.preventDefault();
    if (busy) {
      return;
    }

    const seletor = PrivacySelectorSchema.safeParse({
      type: selectorType,
      value: selectorValue.trim(),
    });
    if (!seletor.success) {
      setOk(false);
      setProblem(
        `O valor não está no formato de "${SELETOR_LABEL[selectorType]}". Recusamos em vez de tentar corrigir: um seletor consertado aponta para outra pessoa.`,
      );
      return;
    }

    // `datetime-local` entrega hora local sem fuso. A conversão para UTC é
    // explícita porque prazo legal contado sobre formato solto é prazo que
    // ninguém consegue defender depois.
    const instante = new Date(receivedAt);
    if (Number.isNaN(instante.getTime())) {
      setOk(false);
      setProblem('Informe quando o pedido chegou.');
      return;
    }

    const corpo = CreatePrivacyRequestSchema.safeParse({
      right,
      selector: seletor.data,
      receivedVia,
      receivedAt: instante.toISOString(),
    });
    if (!corpo.success) {
      setOk(false);
      setProblem('Os dados do pedido não fecham. Confira antes de abrir.');
      return;
    }

    setBusy(true);
    setProblem(null);
    setOk(false);
    void dataSource
      .createPrivacyRequest(corpo.data)
      .then(() => {
        setSelectorValue('');
        setReceivedAt('');
        setOk(true);
        onCreated();
      })
      .catch((erro: unknown) => {
        setProblem(describeProblem(erro).message);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <section className="dm-panel" aria-labelledby={headingId}>
      <div className="dm-panel-head">
        <h2 id={headingId}>Abrir pedido</h2>
        <span className="dm-illustrative">ART. 18 LGPD</span>
      </div>

      <form className="dm-form" onSubmit={submit} noValidate>
        <div className="dm-field">
          <label htmlFor={rightId}>Direito exercido</label>
          <select
            id={rightId}
            value={right}
            disabled={busy}
            onChange={(event) => {
              setRight(event.target.value as PrivacyRight);
            }}
          >
            {PrivacyRightSchema.options.map((item) => (
              <option key={item} value={item}>
                {DIREITO_LABEL[item]}
              </option>
            ))}
          </select>
        </div>

        <div className="dm-field">
          <label htmlFor={selectorTypeId}>Como o pedido aponta a pessoa</label>
          <select
            id={selectorTypeId}
            value={selectorType}
            disabled={busy}
            onChange={(event) => {
              setSelectorType(event.target.value as SelectorType);
              setSelectorValue('');
            }}
          >
            {(Object.keys(SELETOR_LABEL) as readonly SelectorType[]).map(
              (item) => (
                <option key={item} value={item}>
                  {SELETOR_LABEL[item]}
                </option>
              ),
            )}
          </select>
        </div>

        <div className="dm-field">
          <label htmlFor={selectorValueId}>{SELETOR_LABEL[selectorType]}</label>
          <input
            id={selectorValueId}
            type="text"
            value={selectorValue}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              setSelectorValue(event.target.value);
            }}
          />
        </div>

        <div className="dm-field">
          <label htmlFor={viaId}>Canal em que o pedido chegou</label>
          <select
            id={viaId}
            value={receivedVia}
            disabled={busy}
            onChange={(event) => {
              setReceivedVia(event.target.value as keyof typeof CANAL_LABEL);
            }}
          >
            {(
              Object.keys(CANAL_LABEL) as readonly (keyof typeof CANAL_LABEL)[]
            ).map((item) => (
              <option key={item} value={item}>
                {CANAL_LABEL[item]}
              </option>
            ))}
          </select>
        </div>

        <div className="dm-field">
          <label htmlFor={receivedAtId}>Quando o pedido chegou</label>
          <input
            id={receivedAtId}
            type="datetime-local"
            value={receivedAt}
            disabled={busy}
            onChange={(event) => {
              setReceivedAt(event.target.value);
            }}
          />
        </div>

        <div className="dm-actions">
          <button
            className="dm-action dm-action-primary"
            type="submit"
            disabled={busy}
          >
            Abrir pedido
          </button>
        </div>
      </form>

      {problem ? (
        <div className="dm-alert" role="alert">
          {problem}
        </div>
      ) : null}

      {ok ? (
        <p className="dm-toast" role="status">
          Pedido aberto. O prazo legal já está correndo, e a execução continua
          bloqueada até o titular provar que é ele.
        </p>
      ) : null}
    </section>
  );
};
